import EventEmitter from "eventemitter3";

/**
 * @see https://docs.unity3d.com/ScriptReference/MonoBehaviour.html
 */
export class MonoBehaviour extends EventEmitter {

    static EVENT = Object.freeze({
        START: "appStart",
        UPDATE: "appUpdate",
        FIXED_UPDATE: "appFixedUpdate",
        LATE_UPDATE: "appLateUpdate",
        GUI: "appOnGUI",
        DISABLE: "monoOnDisable",
        ENABLE: "monoOnEnable"
    });

    static events = new EventEmitter();
    #enabled = true;

    constructor() {
        super();

        this.__Start = this.__Start.bind(this);
        this.__Update = this.__Update.bind(this);
        this.__FixedUpdate = this.__FixedUpdate.bind(this);
        this.__LateUpdate = this.__LateUpdate.bind(this);
        this.__OnGUI = this.__OnGUI.bind(this);
        this.__OnDisable = this.__OnDisable.bind(this);
        this.__OnEnable = this.__OnEnable.bind(this);

        this.__addMonoBehaviourListeners();
        this.__Awake();
    }

    get enabled() {
        return this.#enabled;
    }

    set enabled(value) {
        value = !!value;
        let changed = value ^ this.enabled;
        this.#enabled = value;
        if (changed) this.emit(value
            ? MonoBehaviour.EVENT.ENABLE
            : MonoBehaviour.EVENT.DISABLE
        );
    }

    get isActiveAndEnabled() {
        // https://docs.unity3d.com/ScriptReference/Behaviour-isActiveAndEnabled.html
        return this.#enabled;
    }

    static emit(event, ...args) {
        return this.events.emit(event, ...args);
    }

    __addMonoBehaviourListeners() {
        MonoBehaviour.events.on(MonoBehaviour.EVENT.START, this.__Start);
        MonoBehaviour.events.on(MonoBehaviour.EVENT.UPDATE, this.__Update);
        MonoBehaviour.events.on(MonoBehaviour.EVENT.FIXED_UPDATE, this.__FixedUpdate);
        MonoBehaviour.events.on(MonoBehaviour.EVENT.LATE_UPDATE, this.__LateUpdate);
        MonoBehaviour.events.on(MonoBehaviour.EVENT.GUI, this.__OnGUI);
        this.on(MonoBehaviour.EVENT.DISABLE, this.__OnDisable);
        this.on(MonoBehaviour.EVENT.ENABLE, this.__OnEnable);
    }

    __removeMonoBehaviourListeners() {
        MonoBehaviour.events.off(MonoBehaviour.EVENT.START, this.__Start);
        MonoBehaviour.events.off(MonoBehaviour.EVENT.UPDATE, this.__Update);
        MonoBehaviour.events.off(MonoBehaviour.EVENT.FIXED_UPDATE, this.__FixedUpdate);
        MonoBehaviour.events.off(MonoBehaviour.EVENT.LATE_UPDATE, this.__LateUpdate);
        MonoBehaviour.events.off(MonoBehaviour.EVENT.GUI, this.__OnGUI);
        this.off(MonoBehaviour.EVENT.DISABLE, this.__OnDisable);
        this.off(MonoBehaviour.EVENT.ENABLE, this.__OnEnable);
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.Awake.html
    // Use Awake to initialize variables or states before the application starts.
    // Unity calls Awake only once during the lifetime of the script instance.
    __Awake(...args) {
        if (this.Awake) {
            this.Awake(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.Start.html
    // Start is called on the frame when a script is enabled just before any of the Update methods are called the first time.
    // The Awake function is called on all objects in the Scene before any object's Start function is called
    __Start(...args) {
        if (this.Start) {
            this.Start(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.Update.html
    // Update is called every frame, if the MonoBehaviour is enabled.
    __Update(...args) {
        if (this.Update) {
            this.Update(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.FixedUpdate.html
    // Frame-rate independent MonoBehaviour.FixedUpdate message for physics calculations.
    __FixedUpdate(...args) {
        if (this.FixedUpdate) {
            this.FixedUpdate(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.LateUpdate.html
    // LateUpdate is called every frame, if the Behaviour is enabled.
    // LateUpdate is called after all Update functions have been called.
    __LateUpdate(...args) {
        if (this.LateUpdate) {
            this.LateUpdate(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.OnGUI.html
    // OnGUI is called for rendering and handling GUI events.
    __OnGUI(...args) {
        if (this.OnGUI) {
            this.OnGUI(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.OnDisable.html
    // This function is called when the behaviour becomes disabled.
    __OnDisable(...args) {
        if (this.OnDisable) {
            this.OnDisable(...args);
        }
    }

    // https://docs.unity3d.com/ScriptReference/MonoBehaviour.OnEnable.html
    // This function is called when the object becomes enabled and active.
    __OnEnable(...args) {
        if (this.OnEnable) {
            this.OnEnable(...args);
        }
    }

    Awake() {
    }

    Start() {
    }

    Update() {
    }

    FixedUpdate() {
    }

    LateUpdate() {
    }

    OnGUI() {
    }

    OnDisable() {
    }

    OnEnable() {
    }


}
